import { Badge } from "@bokio/designsystem/components/Badge/Badge";
import { Article } from "@bokio/designsystem/components/TypographicElements/TypographicElements";
import PageTitle from "@bokio/elements/PageTitle/PageTitle";
import { mergeClassNames } from "@bokio/utils/classes";

import { LayoutFull } from "../LayoutFull/LayoutFull";

import type { BadgeColor } from "@bokio/designsystem/components/Badge/Badge";
import type { FC, ReactNode } from "react";

import * as styles from "./singleFormPage.scss";

type Width = "narrow" | "wide";

export interface SingleFormPageProps {
	/**
	 * This is displayed at the top of the box content of the page.
	 * We use it in the sign up flows for indicating the current step.
	 */
	indicator?: ReactNode;

	illustration?: ReactNode;

	title: string;

	/**
	 * This is usually a short text that is added right below the title.
	 */
	preamble?: ReactNode;

	children?: ReactNode;

	/**
	 * The epilogue is added below the main page box and is used for extra content in the page.
	 * We use it, for example, for displaying which country the user is logging in to.
	 */
	epilogue?: ReactNode;

	/**
	 * This is a bar that is appended to the bottom of the page's box, but it's still part of the page's main content.
	 */
	contentFooter?: ReactNode;

	/**
	 * @deprecated add props to handle the change instead of using className
	 */
	className?: string;

	pageTitle?: string;

	status?: { name: string; color: BadgeColor };

	/** Should be set for pages on GuestRoute, will show popup help rather than topbar */
	noTopBarMode: boolean;

	/**
	 * Determines whether to show the title.
	 * Defaults to `true` if not provided.
	 */
	showTitle?: boolean;

	width?: Width;
}

const SingleFormPage: FC<SingleFormPageProps> = ({
	indicator,
	illustration,
	title,
	preamble,
	children,
	epilogue,
	contentFooter,
	className,
	pageTitle,
	status,
	noTopBarMode,
	showTitle = true, // Default value set here
	width = "narrow",
}) => (
	<LayoutFull noTopBarMode={noTopBarMode}>
		<PageTitle title={pageTitle || title} />
		<Article resetHeadingLevelTo={1}>
			<div className={mergeClassNames(styles.signUp, width === "wide" && styles.signUpWide, className)}>
				<div className={styles.content}>
					{indicator && <div className={styles.indicator}>{indicator}</div>}
					{illustration && <div className={styles.illustration}>{illustration}</div>}
					<div className={styles.header}>
						{status && <Badge {...status} noMargin />}
						{showTitle && <h1 className={status && styles.smallerMarginHeader}>{title}</h1>}
						{preamble && <div className={styles.preamble}>{preamble}</div>}
					</div>
					{children}
				</div>
				{contentFooter && <div className={styles.contentFooter}>{contentFooter}</div>}
			</div>
		</Article>
		{epilogue && <div className={styles.epilogue}>{epilogue}</div>}
	</LayoutFull>
);

export default SingleFormPage;
